import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Mic, Store, Heart, Trophy, Gift, BookOpen, Users, Sparkles, Building2, CreditCard, ArrowUpRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { haptic } from "../lib/haptics";

const U = (id) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=80&w=900`;

const FEATURED = [
  { to: "/live", title: "Locked In Live", sub: "Watch weddings in real time", icon: Radio, img: U("photo-1712314947761-a8d718bd8c32") },
  { to: "/podcast", title: "The Podcast", sub: "Real talk with Mr. & Mrs. Don", icon: Mic, img: U("photo-1718463384055-738905bdd16e") },
  { to: "/contest", title: "$25,000 Wedding Contest", sub: "Enter, get votes, win", icon: Gift, img: U("photo-1780593194924-35f0343e738b") },
  { to: "/invite-trend", title: "#lockedinforever", sub: "Your invite photo, +25 contest votes", icon: Gift, img: U("photo-1780593194924-35f0343e738b") },
  { to: "/vendors", title: "Find Your Pros", sub: "Planners, venues & vendors", icon: Store, img: U("photo-1766393195987-912865cbb81b") },
];

const TILES = [
  { to: "/community", title: "Community", icon: Users },
  { to: "/community/directory", title: "Find Pros", icon: Store },
  { to: "/feed", title: "Wedding Feed", icon: Sparkles },
  { to: "/featured", title: "Hall of Fame", icon: Trophy },
  { to: "/club", title: "Couples Club", icon: Users },
  { to: "/therapy", title: "Couples Therapy", icon: Heart },
  { to: "/blog", title: "Journal", icon: BookOpen },
  { to: "/pricing", title: "Pricing", icon: CreditCard },
];

const rise = (i) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.05 * i, duration: 0.45, ease: "easeOut" } });

function FeatureCard({ f, i }) {
  return (
    <motion.div {...rise(i)} className="snap-start shrink-0 w-[78%] sm:w-[46%] lg:w-auto">
      <Link to={f.to} onClick={() => haptic()} data-testid={`explore-featured-${f.to.slice(1)}`}
        className="group relative block aspect-[4/5] lg:aspect-[3/4] rounded-3xl overflow-hidden bg-[var(--ink)]">
        <img src={f.img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center"><f.icon className="w-4 h-4 text-white" /></div>
        <ArrowUpRight className="absolute top-4 right-4 w-5 h-5 text-white/70 group-hover:text-[var(--gold)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-[color,transform]" />
        <div className="absolute bottom-0 p-5 text-white">
          <p className="font-serif text-2xl leading-tight">{f.title}</p>
          <p className="text-xs text-white/70 mt-1">{f.sub}</p>
        </div>
      </Link>
    </motion.div>
  );
}

export default function ExplorePage() {
  return (
    <div className="bg-[var(--bg)] min-h-screen" data-testid="explore-page">
      <Seo title="Explore LockedInForever" description="Live weddings, the podcast, the $25,000 wedding contest, wedding pros and the community — all in one place." path="/explore" />
      <Navbar />
      <section className="container-wide px-5 md:px-12 pt-8 md:pt-14">
        <p className="font-script text-[var(--gold)] text-4xl">Explore</p>
        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl">Everything forever.</h1>
      </section>
      <section className="mt-8">
        <div className="flex lg:grid lg:grid-cols-4 gap-4 overflow-x-auto snap-x snap-mandatory px-5 md:px-12 container-wide pb-2 no-scrollbar" data-testid="explore-featured">
          {FEATURED.map((f, i) => <FeatureCard key={f.to} f={f} i={i} />)}
        </div>
      </section>
      <section className="container-wide px-5 md:px-12 py-10 md:py-16">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] mb-4">More to discover</p>
        <div className="grid grid-cols-4 lg:grid-cols-8 gap-3">
          {TILES.map((t, i) => (
            <motion.div key={t.to} {...rise(i + 4)}>
              <Link to={t.to} onClick={() => haptic()} data-testid={`explore-tile-${t.to.slice(1)}`}
                className="group flex flex-col items-center gap-2 text-center active:scale-95 transition-transform">
                <span className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white border border-[var(--line)] flex items-center justify-center group-hover:border-[var(--gold)] group-hover:-translate-y-1 group-hover:shadow-[0_12px_30px_rgba(200,169,107,0.25)] transition-[border-color,transform,box-shadow] duration-300">
                  <t.icon className="w-5 h-5 text-[var(--gold)]" strokeWidth={1.6} />
                </span>
                <span className="text-[11px] leading-tight text-[var(--ink)]">{t.title}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
